/* The scroll stage.
 *
 * One large cartridge crosses the screen while you scroll. It rotates as it
 * travels, so the motion reads as rolling rather than sliding, and when it
 * reaches the far side it crossfades into the next product and travels back the
 * other way. Five products, five crossings, and scroll position is the only
 * input: stop scrolling and it stops, scroll back and it runs backwards.
 *
 * It is the same 36-frame turntables the product pages use, driven off scroll
 * instead of a drag, which is why it can be this cheap. No 3D, no model, no
 * library: it is real photography of a real cartridge, so it cannot be wrong
 * about what the product looks like.
 *
 * Four things here are carried over from the turntable work because each one
 * has already failed once on this project:
 *
 *   1. Frames are painted to a canvas, never swapped into an <img> src. Setting
 *      src per frame blanks the element while each frame decodes.
 *   2. Rotation is capped at plus or minus 45 degrees off the front face.
 *      Measured on these exact packs: 30 keeps the wordmark dominant, 45 is
 *      where it starts to wrap out of frame, and 60 already shows back-panel
 *      legal print on the red and the blue. So 45 is the ceiling, and the front
 *      face lands dead centre of each crossing, which is the moment the tube is
 *      largest and nearest the middle of the screen.
 *   3. Relative size is taken from the turntables' own geometry, never from
 *      pixels. Five turntables were shot at five camera distances, so meta.cyl
 *      over meta.canvas is the only thing that makes a 300 mL cartridge the
 *      same size as another 300 mL cartridge. Same maths as spin.js.
 *   4. The standing line is held. Every tube stands on one line across all five
 *      crossings, or they appear to hop as the product changes.
 *
 * Progressive enhancement. The markup ships a static row of all five at their
 * front faces. This only replaces it once the first product's frames have
 * decoded, so with JS off, with the frames blocked, or on a reduced-motion
 * setting, the page still shows the five products correctly and the section
 * collapses to its natural height.
 */
(function () {
  'use strict';

  var nbnA = window.nbnAsset || function (p) { return p; };
  var stage = document.querySelector('[data-stage]');
  if (!stage) return;
  if (!document.createElement('canvas').getContext) return;
  if (matchMedia('(prefers-reduced-motion:reduce)').matches) return;

  var items;
  try { items = JSON.parse(stage.getAttribute('data-stage')); }
  catch (e) { return; }
  if (!items.length) return;

  var box = stage.querySelector('.stage-fix');
  var statik = stage.querySelector('.stage-static');
  var labels = Array.prototype.slice.call(stage.querySelectorAll('.stage-label'));
  var dots = Array.prototype.slice.call(stage.querySelectorAll('.stage-rail button'));
  if (!box) return;

  var N = items.length;
  var FADE = 0.14;        // share of a crossing spent handing over to the next
  /* Frames of rotation across one crossing, at 10 degrees per frame. 72 is two
     full revolutions, and the crossing STARTS on the front face.

     Starting on the front is the requirement: the first thing on screen at the
     top of the page, and the first thing on screen each time the product
     changes, has to be the label rather than the back of the pack.

     Two turns rather than one because of where that leaves the back panel, which
     is dense legal print, a barcode and a QR code. Any full revolution has to
     pass through it once. At one turn the front is at both ends and the back
     lands at u=0.5, the middle of the crossing, where the tube is largest and
     most central: the worst possible place for it. At two turns the front face
     lands three times, at the start, the middle and the end, so it is what you
     see both at the handover and at the tube's biggest moment, and the back
     passes through at the quarter points instead, mid-travel and moving.

     Rotation used to be centred on the front face and capped at plus or minus 45
     degrees to keep the back off screen entirely. The owner asked twice for a
     spin, so the cap is gone; this is the arrangement that spins and still leads
     with the label. */
  var TURN = 72;
  var TRAVEL = 0.32;      // half the crossing, as a share of the stage width
  var GROW = 0.20;        // how much smaller at the edges than in the middle
  var FOOT = 10;          // gap between the standing line and the rail

  /* ------------------------------------------------------------------ frames */

  /* Every set's front frame is fetched first, all five of them, then the full
     36 of each set in order. That ordering is what makes the stage usable
     immediately: a crossing whose set has not finished still travels and still
     scales, it just holds its front face until the rotation arrives. Waiting
     for all 180 frames before drawing anything would leave the hero empty for
     several seconds on a slow connection. */
  var sets = items.map(function (it) {
    return {
      m: it.meta, dir: it.dir, imgs: new Array(it.meta.n), ready: false,
      /* Rotation the reader has added by hand, in frames, on top of whatever the
         scroll position asks for. Kept per product, so spinning the red tube
         does not also spin the blue one, and so scrolling away and back finds it
         where it was left. */
      spin: 0
    };
  });

  /* The tallest tube, measured in the shared canvas space, and the reference the
     whole stage is scaled against.

     meta.canvas is 1857 for every set, and using it directly is correct but
     wasteful: it is the height of the original hero PNG, which carried empty
     space above the nozzle, so scaling to it left 8 percent of the band unused.
     This measures the tubes themselves instead. drawnHeight is
     disp * h * cyl / bodyw, so h * cyl / bodyw is each tube's height per unit of
     disp, and the largest of those is what has to fit. Derived rather than
     hard-coded so it stays right if a turntable is ever reshot. */
  var REF = Math.max.apply(null, items.map(function (it) {
    var m = it.meta;
    return m.h * m.cyl / m.bodyw;
  }));

  var canvas = document.createElement('canvas');
  canvas.className = 'stage-canvas';
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label',
    'The five cartridges in turn, each rotating as it crosses the screen. Scroll to move them.');
  var ctx = canvas.getContext('2d');

  function load(src) {
    return new Promise(function (res) {
      var im = new Image();
      im.onload = function () { res(im); };
      im.onerror = function () { res(null); };
      im.src = src;
    });
  }

  /* MOBILE GETS A SMALLER SET, BECAUSE THE STAGE DRAWS THESE TINY.
   *
   * Measured on a 375pt viewport at DPR2: each tube is drawn at 90 x 439 DEVICE
   * pixels from a 288 x 1400 source, so about a tenth of the pixels shipped are
   * used. Four sets of 36 came to 9.7 MB, which is most of what this page asked
   * a phone to download.
   *
   * The '-sm' sets are the same frames at 768 tall, which still has headroom for
   * a 430pt phone at DPR3 (155 x 755). Chosen by the same 900px breakpoint the
   * CSS uses so the two cannot drift.
   *
   * The picker turntables further down the page keep the full-size sets: they
   * render at 154 CSS px, which is 308 device pixels at DPR2, and a 158px frame
   * would visibly soften a product the reader is being asked to choose.
   */
  var small = matchMedia('(max-width: 900px)').matches;

  /* THESE PATHS MUST APPEAR LITERALLY, IN QUOTES, SOMEWHERE IN THE SHIPPED JS.
     bundle.py's prune_unreferenced keeps an asset directory only when its path
     is followed by a quote in the packaged HTML/CSS/JS, precisely because the
     spin viewer builds frame names itself. A directory reached only by a
     runtime .replace() is invisible to that scan, so all 144 small frames would
     be deleted on the next rebuild and the stage would quietly fall back to the
     9.7 MB full sets. This array is also the real whitelist of which sets have
     a small variant, so it is load-bearing twice over. */
  var SM_SETS = ['assets/products/360/ap-sm/', 'assets/products/360/clr-sm/',
                 'assets/products/360/kb-sm/', 'assets/products/360/wds-sm/'];

  function smallDir(dir) {
    // dirPath is '../assets/...' in the source tree and 'assets/...' in the
    // package, so match on the trailing product segment rather than the whole
    // path.
    var base = dir.replace(/\/$/, '').split('/').pop();
    for (var i = 0; i < SM_SETS.length; i++) {
      if (SM_SETS[i].split('/').slice(-2)[0] === base + '-sm') {
        return dir.replace(/([^/]+)\/$/, '$1-sm/');
      }
    }
    return null;
  }

  function frameSrc(it, i) {
    var dir = it.dirPath;
    if (small && it.smDir !== false) {
      dir = smallDir(dir) || dir;
    }
    return nbnA(dir + 'f' + String(i).padStart(2, '0') + '.webp');
  }

  var started = false;

  // Pass one: the front face of each, so all five can be drawn at once.
  Promise.all(items.map(function (it, i) {
    return load(frameSrc(it, it.meta.front)).then(function (im) {
      /* Fall back to the full set if the small one is not there. The '-sm' dirs
         are generated, not authored, so a tree that has not been rebuilt must
         degrade to a heavier page rather than an empty one. */
      if (!im && small) {
        it.smDir = false;
        return load(frameSrc(it, it.meta.front)).then(function (im2) {
          if (im2) sets[i].imgs[it.meta.front] = im2;
        });
      }
      if (im) sets[i].imgs[it.meta.front] = im;
    });
  })).then(function () {
    if (!sets[0].imgs[items[0].meta.front]) return;   // keep the static row
    begin();
    // Pass two: everything else, set by set, in the order they are scrolled to.
    return items.reduce(function (chain, it, i) {
      return chain.then(function () {
        var rest = [];
        for (var f = 0; f < it.meta.n; f++) {
          if (f !== it.meta.front) rest.push(f);
        }
        return Promise.all(rest.map(function (f) {
          return load(frameSrc(it, f)).then(function (im) { if (im) sets[i].imgs[f] = im; });
        })).then(function () {
          sets[i].ready = sets[i].imgs.every(Boolean);
          request();
        });
      });
    }, Promise.resolve());
  });

  /* ------------------------------------------------------------------ layout */

  var W = 0, H = 0, dpr = 1, avail = 0, narrow = false;

  function measure() {
    var r = box.getBoundingClientRect();
    W = Math.round(r.width);
    H = Math.round(r.height);

    /* The band the tube may occupy is whatever sits above the lowest thing the
       copy has claimed. Both obstacles are MEASURED, never assumed:

       The rail, because it is shorter and lower on a phone, and the two tallest
       cartridges reach it first, so a hard-coded reserve would have looked like
       a bug in two products out of five rather than a layout constant.

       The labels, because at narrow widths they stop being a column beside the
       tube and become a band underneath it. Which of the two it is is decided
       by asking whether a label is wide enough to be in the tube's way, not by
       repeating the CSS breakpoint here where it would silently drift out of
       step with the stylesheet. Hidden elements still have a box, so this reads
       true for all five labels including the one that wraps to two lines. */
    var floorY = r.bottom;
    var rail = stage.querySelector('.stage-rail');
    if (rail) floorY = Math.min(floorY, rail.getBoundingClientRect().top);

    narrow = false;
    labels.forEach(function (el) {
      var lr = el.getBoundingClientRect();
      if (lr.width > W * 0.55) {          // spans the stage: it is a bottom band
        narrow = true;
        floorY = Math.min(floorY, lr.top);
      }
    });

    avail = Math.max(160, Math.round(floorY - r.top - 8));
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /* Progress across the whole stage, 0 to 1. The stage is taller than the
     sticky box by exactly the scroll distance we want, so the travelled
     fraction is the sticky box's offset within its own parent. */
  function progress() {
    var r = stage.getBoundingClientRect();
    var run = stage.offsetHeight - H;
    if (run <= 0) return 0;
    return Math.min(1, Math.max(0, -r.top / run));
  }

  /* ------------------------------------------------------------------- paint */

  function paintOne(i, u, alpha) {
    var s = sets[i];
    var m = s.m;
    if (alpha <= 0.002) return;

    /* Which frame. u=0 is the front face, then two full turns across the
       crossing, in the direction of travel so the tube rolls rather than spinning
       on the spot. s.spin is whatever the reader has added by dragging on top of
       that. Neither is clamped. */
    var k = m.front + s.dir * u * TURN + s.spin;
    k = ((Math.round(k) % m.n) + m.n) % m.n;
    var im = s.imgs[k] || s.imgs[m.front];
    if (!im) return;

    var grow = 1 - GROW * Math.abs(u - 0.5);
    /* disp is derived from meta.canvas, which is 1857 for all five sets, so one
       expression scales all of them and the relative sizes stay true: a 300 mL
       cartridge ends up the same size as another 300 mL cartridge even though
       the five turntables were shot at five different camera distances. Never
       normalise on height. The clear tube genuinely stands about 11 percent
       shorter and equalising that would make it the wrong product. */
    var disp = (avail - FOOT) / REF;               // canvas space to CSS pixels
    var scale = ((m.cyl * disp) / m.bodyw) * grow; // source frame to CSS pixels

    var x = W / 2 + s.dir * (W * TRAVEL) * (2 * u - 1);
    /* One standing line for all five crossings, sitting just above the rail. If
       this were centred per tube instead, the product would visibly hop each
       time the tube changed. */
    // meta.base is the standing line inside the source frame, so subtracting it
    // puts the frame's own feet on our line whatever the frame's overall height.
    var top = (avail - FOOT) - m.base * scale;

    ctx.globalAlpha = alpha;
    ctx.drawImage(im, x - m.cx * scale, top, m.w * scale, m.h * scale);
    ctx.globalAlpha = 1;

    // Drag sensitivity is measured in tube widths, not screen widths, so the
    // gesture feels like turning the object rather than dragging the page.
    grabW = m.cyl * disp;
  }
  var grabW = 100;

  var lastSeg = -1;
  var activeSeg = 0;       // what a drag is currently holding

  function paint() {
    if (!W || !H) return;
    var p = progress();
    var seg = Math.min(N - 1, Math.floor(p * N));
    var u = Math.min(1, Math.max(0, p * N - seg));
    activeSeg = seg;

    ctx.clearRect(0, 0, W, H);

    /* The handover happens in the tail of a crossing, not across the seam
       between two. Both tubes are at the same far-side position at that point,
       which is the whole idea: the tube arrives at one edge and becomes the
       next product there, rather than one sliding out while another slides in. */
    /* No intro to work around any more. The headline sits above the pinned box
       rather than over it, so the tube is drawn at full strength from the first
       pixel of the first crossing. That removed three interlocking workarounds:
       an intro fade, an alpha trade on the tube, and a rotation-phase remap that
       existed only because the tube had been hidden. On a phone the trade was
       what left the top of this page blank. */
    if (u > 1 - FADE && seg < N - 1) {
      var k = (u - (1 - FADE)) / FADE;
      paintOne(seg, u, 1 - k);
      paintOne(seg + 1, 0, k);
      /* Past the halfway point of the crossfade the incoming tube is the one you
         are actually looking at, so the rail should name it and a drag should
         turn it. Switching on the segment boundary instead leaves the rail
         pointing at the outgoing product for the last 14 percent of every
         crossing, which is long enough to read as the rail being wrong. */
      if (k > 0.5) activeSeg = seg + 1;
    } else {
      paintOne(seg, u, 1);
    }

    labels.forEach(function (el, i) {
      var a = 0;
      if (i === seg) {
        /* On a wide screen the label is a column beside the tube on the side the
           tube sets off from, so it has to wait for the tube to get out of the
           way. On a phone it is a band underneath and there is nothing to wait
           for, so it comes in almost at once: the label is the main thing telling
           a phone reader which of the five they are looking at. Either way the
           first crossing also waits for the headline to finish. */
        // Wide: the label is a column on the side the tube leaves, so it waits
        // for the tube to clear. Narrow: it is a band underneath with nothing to
        // wait for, and it is the only thing naming the product.
        var inAt = narrow ? 0.03 : 0.16;
        a = Math.min(1, Math.max(0, (u - inAt) / 0.14)) *
            Math.min(1, Math.max(0, (0.94 - u) / 0.14));
      }
      el.style.opacity = a;
      el.style.visibility = a < 0.02 ? 'hidden' : 'visible';
    });

    if (activeSeg !== lastSeg) {
      lastSeg = activeSeg;
      dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === activeSeg ? 'true' : 'false');
      });
    }
  }

  /* ------------------------------------------------------------------ drive */

  var queued = false;

  function request() {
    if (queued) return;
    queued = true;
    /* rAF does not fire while the page is hidden and can stay starved in an
       embedded pane, which would freeze the stage on one frame with nothing to
       show for it. So the timeout is a real fallback, not a belt-and-braces
       gesture, and whichever lands first cancels the other. */
    var raf = requestAnimationFrame(run);
    var timer = setTimeout(function () { cancelAnimationFrame(raf); run(); }, 120);
    function run() {
      clearTimeout(timer);
      queued = false;
      paint();
    }
  }

  function begin() {
    if (started) return;
    started = true;
    box.insertBefore(canvas, box.firstChild);
    stage.classList.add('is-live');
    if (statik) statik.setAttribute('aria-hidden', 'true');
    measure();
    paint();

    addEventListener('scroll', request, { passive: true });
    addEventListener('resize', function () { measure(); paint(); });
    if (window.ResizeObserver) new ResizeObserver(function () { measure(); paint(); }).observe(box);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) request(); });

    bindDrag();

    /* The rail is the non-scroll way in, and the reason it exists: a page whose
       only control is the scrollbar is unusable to anyone driving it from the
       keyboard, and it gives the section a table of contents besides. Each
       button scrolls to the middle of its crossing, where that product's tube
       is centred, largest, and facing camera. */
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () {
        var run = stage.offsetHeight - H;
        var target = stage.getBoundingClientRect().top + window.pageYOffset
                   + run * ((i + 0.5) / N);
        window.scrollTo({ top: target, behavior: 'smooth' });
      });
    });
  }

  /* ------------------------------------------------------------------- drag */

  /* Swipe across the tube to turn it, on top of whatever the scroll is doing.
     Scroll moves it across the screen; a drag spins it on the spot.

     The two gestures have to coexist on a phone, where a swipe is also how you
     scroll. touch-action: pan-y in the stylesheet is what splits them: the
     browser keeps vertical panning, which is the page scroll and therefore the
     crossing, and hands us the horizontal component, which is the rotation. So
     a vertical swipe still scrolls the page normally and never gets swallowed.

     Sensitivity is a fraction of the tube's own drawn width rather than of the
     viewport, which is what makes it feel like turning the object instead of
     scrubbing a timeline. It works out at roughly one full turn per 320px of
     drag: a comfortable thumb swipe is about half a turn, which is slow enough
     to stop on a face deliberately. The product pages spin a whole turn in one
     tube width, and matching that ratio here, where the tube is five times
     bigger on screen, sent it most of the way round on a single short drag.

     After the reader lets go and the throw has run out, the rotation eases back
     to the scroll baseline. The original reason was to keep the tube off its back
     panel, which no longer applies now the scroll sweeps through that panel
     anyway. What it still does is restore the choreography: a drag permanently
     offsets that product's phase, so without this the front face stops landing in
     the middle of the crossing and every later scroll through it is off. The
     grace period is long enough that it never interrupts someone still playing
     with it. */
  function bindDrag() {
    var id = null, lastX = 0, lastT = 0, vel = 0, spinning = 0, settling = 0, idle = 0;
    var startX = 0, startY = 0, axis = 0;   // axis: 0 undecided, 1 turning, -1 given up

    // A full turn in about 160px of drag, which is one and a half tube widths.
    // 12 gave 320px per turn and read as a nudge rather than a spin.
    function perFrame() { return Math.max(1.5, grabW / 24); }

    /* Fold the accumulated rotation into the nearest half turn either way, so
       easing back travels the short way round. Without this, a tube spun 300
       degrees forwards unwinds 300 degrees backwards instead of going 60 more. */
    function shorten(seg) {
      var n = sets[seg].m.n;
      var v = sets[seg].spin % n;
      if (v > n / 2) v -= n;
      if (v < -n / 2) v += n;
      sets[seg].spin = v;
    }

    function stopSettling() {
      clearTimeout(idle);
      cancelAnimationFrame(settling);
    }

    function settleSoon(seg) {
      stopSettling();
      idle = setTimeout(function () {
        shorten(seg);
        var prev = 0;
        function step(now) {
          if (!prev) prev = now;
          var dt = Math.min(now - prev, 60) / 1000;
          prev = now;
          var v = sets[seg].spin;
          sets[seg].spin = v + (0 - v) * Math.min(1, dt * 6);
          if (Math.abs(sets[seg].spin) < 0.05) sets[seg].spin = 0;
          paint();
          if (sets[seg].spin !== 0) settling = requestAnimationFrame(step);
        }
        settling = requestAnimationFrame(step);
      }, 1800);
    }

    canvas.tabIndex = 0;

    canvas.addEventListener('pointerdown', function (e) {
      id = e.pointerId;
      lastX = startX = e.clientX;
      startY = e.clientY;
      lastT = e.timeStamp;
      axis = 0;
      vel = 0;
      cancelAnimationFrame(spinning);
      stopSettling();
      try { canvas.setPointerCapture(id); } catch (err) { /* not fatal */ }
      stage.classList.add('is-grabbing');
    });

    canvas.addEventListener('pointermove', function (e) {
      if (e.pointerId !== id) return;

      /* Axis lock. touch-action: pan-y already means a browser hands a clearly
         vertical touch gesture to the scroller and cancels our pointer, so this
         is for the cases it does not: a mouse drag, and the first few pixels of
         a touch before the browser has decided. Without it a scroll flick with a
         few pixels of sideways wobble nudges the rotation, which reads as the
         tube twitching on its own. Once a gesture has been given up on it stays
         given up until the next pointerdown, so a drag cannot change its mind
         halfway and jump. */
      if (axis === 0) {
        var tx = Math.abs(e.clientX - startX);
        var ty = Math.abs(e.clientY - startY);
        if (tx < 5 && ty < 5) return;
        axis = tx > ty ? 1 : -1;
        lastX = e.clientX;
        lastT = e.timeStamp;
        if (axis === -1) return;
      } else if (axis === -1) {
        return;
      }

      e.preventDefault();
      var dx = e.clientX - lastX;
      var dt = Math.max(1, e.timeStamp - lastT);
      var df = -dx / perFrame();
      sets[activeSeg].spin += df;
      stage.classList.add('has-spun');     // only once something actually turned
      // Frames per second, for the throw. Clamped so one stray sample cannot
      // launch the tube into a blur.
      vel = Math.max(-90, Math.min(90, (df / dt) * 1000));
      lastX = e.clientX;
      lastT = e.timeStamp;
      request();
    });

    function release(e) {
      if (e.pointerId !== id) return;
      var turned = axis === 1;
      id = null;
      axis = 0;
      stage.classList.remove('is-grabbing');
      if (turned) throwIt();
    }
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);

    /* Let go mid-swipe and it keeps turning, slowing down. Without this a flick
       stops dead the instant your finger leaves the glass, which reads as the
       page having dropped the gesture. */
    function throwIt() {
      var prev = 0;
      var seg = activeSeg;
      function step(now) {
        if (!prev) prev = now;
        var dt = Math.min(now - prev, 60) / 1000;
        prev = now;
        sets[seg].spin += vel * dt;
        vel *= Math.pow(0.02, dt);            // to 2 percent of speed per second
        paint();
        if (Math.abs(vel) > 0.4 && id === null) spinning = requestAnimationFrame(step);
        else settleSoon(seg);
      }
      if (Math.abs(vel) > 0.4) spinning = requestAnimationFrame(step);
      else settleSoon(seg);
    }

    /* Keyboard. Left and right turn the tube; up, down and the page keys are
       left alone so the stage can still be scrolled through from the keyboard,
       which is also what the rail is for. */
    canvas.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      cancelAnimationFrame(spinning);
      stopSettling();
      stage.classList.add('has-spun');
      sets[activeSeg].spin += (e.key === 'ArrowRight' ? -1 : 1);
      paint();
      settleSoon(activeSeg);
    });
  }
}());
